/* eslint-disable react-hooks/rules-of-hooks */
import { useSelector } from "react-redux";
import { DateTime } from "luxon";

import { useBeneficiaryService } from "@/services/beneficiary.service";
import {
  isValidRoadType,
  isValidStreetName,
  isValidStreetNumber,
} from "@/utils/address-validation";
import { decryptAES } from "@/utils/CryptographyUtils";
import { getCodeSecurity } from "@/utils/apiCanal";
import { AX_ORIGIN, BDU_ORIGIN } from "@/utils/consts";
import {
  DOCUMENT_TYPE_CE,
  DOCUMENT_TYPE_DNI,
  getDocumentType,
} from "@/constants/list-document-type";
import helpers from "@/utils/helpers";
import { getProductOptions } from '@/utils/product'

export const useGetInfoBeneficiary = ({
  prefixControl,
  setValue,
  setError,
  setIsValidDocument,
  product,
}) => {
  const { digitalDataProductName } = getProductOptions();
  const isVP = helpers.validateVidaPlus();
  const { userData } = useSelector((state) => state.vida);
  const { useGetPerson } = useBeneficiaryService();

  const { telefono, correo } = userData;

  const getPersonData = async (documentType, documentNumber) => {
    const request = {
      tipoDocumento: String(documentType),
      numeroDocumento: String(documentNumber),
      canal: "vcd",
    };
    setIsValidDocument(true);

    try {
      const { response } = await useGetPerson(request);

      const personData = JSON.parse(
        decryptAES(response.payload, getCodeSecurity())
      );
      const isValidOrigin =
        personData.origen === BDU_ORIGIN || personData.origen === AX_ORIGIN;
      let phone = "";
      let email = "";
      let birthdate = "";

      if (isValidOrigin) {
        const formatDate = personData.fechaNacimiento
          ? DateTime.fromFormat(personData.fechaNacimiento, "yyyy-MM-dd")
          : "";
        birthdate = formatDate ? formatDate.toFormat("dd/MM/yyyy") : "";

        setValue(`${prefixControl}.names`, personData.nombres);
        setValue(`${prefixControl}.lastName`, personData.apellidoPaterno);
        setValue(`${prefixControl}.surName`, personData.apellidoMaterno);
        setValue(`${prefixControl}.birthdate`, birthdate);
        setValue(`${prefixControl}.gender`, personData.sexo);
        setValue(`${prefixControl}.origin`, personData.origen);

        if (personData?.tercero) {
          setValue(`${prefixControl}.numId`, personData.tercero);
        }

        if (birthdate) {
          const age = formatDate.diffNow("years").years * -1;
          phone = age < 18 ? telefono : personData.telefono;
          email = age < 18 ? correo : personData.correo;
        }

        setValue(`${prefixControl}.phone`, phone);
        setValue(`${prefixControl}.email`, email);
      } else {
        setValue(`${prefixControl}.streetType`, "AV");
      }

      const isValidBasicInformation = isValidOrigin
        ? validateBasicInformation({
            personData,
            birthdate,
          })
        : false;
      const isValidUbigeo = isValidOrigin ? validateUbigeo(personData) : false;
      const isValidAdditionalData = !!phone && !!email;

      mappingAnalyticsTags(documentType, isValidOrigin);

      return {
        isValidBasicInformation,
        isValidUbigeo,
        isValidAdditionalData,
      };
    } catch (error) {
      if (Number(documentType) === DOCUMENT_TYPE_DNI.id) {
        setError(`${prefixControl}.documentNumber`, {
          type: "isValidDocument",
          message: "Documento inválido",
        });
        helpers.getTagLog("Documento de identidad inválido", "trackLog");
        setIsValidDocument(false);
      }

      if (Number(documentType) === DOCUMENT_TYPE_CE.id) {
        setValue(`${prefixControl}.streetType`, "AV");
        mappingAnalyticsTags(documentType, false);
      }

      return {
        isValidBasicInformation: false,
        isValidUbigeo: false,
        isValidAdditionalData: false,
      };
    }
  };

  const validateBasicInformation = ({ personData, birthdate }) => {
    const requiredFields = ["nombres", "apellidoPaterno", "sexo"];
    requiredFields.forEach((field) => {
      if (!personData[field]) {
        return false;
      }
    });
    return !!birthdate;
  };

  const validateUbigeo = (personData) => {
    const { ubigeo, nomvia, nrovia, idptipovia } = personData.direccion || {};
    const isValidStreetType = isValidRoadType(idptipovia);

    setValue(
      `${prefixControl}.streetType`,
      isValidStreetType ? idptipovia : "AV"
    );
    setValue(`${prefixControl}.address`, nomvia);
    setValue(`${prefixControl}.streetNumber`, Number(nrovia) || "");

    if (!ubigeo) return false;

    const departmentId = `0${ubigeo.substring(0, 2)}`;
    const provinceId = `0${ubigeo.substring(2, 4)}`;
    const districtId = `0${ubigeo.substring(4, 6)}`;

    setValue(`${prefixControl}.department`, departmentId);
    setValue(`${prefixControl}.province`, provinceId);
    setValue(`${prefixControl}.district`, districtId);

    return (
      isValidStreetType &&
      isValidStreetNumber(nrovia) &&
      isValidStreetName(nomvia)
    );
  };

  const mappingAnalyticsTags = (documentType, isValidOrigin) => {
    const documentTypeLabel = getDocumentType(documentType).label;

    helpers.getTagMetadata(
      isVP
        ? "Seguro Vida Plus - Elegir beneficiarios"
        : `Elegir beneficiarios ${digitalDataProductName}`,
      `${documentTypeLabel} - ${
        isValidOrigin ? "Con datos cliente" : "Sin datos cliente"
      }`,
      "trackMetadata"
    );

    if (!isValidOrigin) {
      helpers.getTagChannel(
        product,
        "Datos personales Beneficiario – Sin datos cliente",
        "trackFormView"
      );
    }
  };

  return {
    getPersonData,
  };
};
